import React from 'react';

type GridColumn = {
  key: string;
  label: string;
};

type GridRow = {
  label: string;
  values: Record<string, React.ReactNode>;
};

type GridSection = {
  id: string;
  columns: GridColumn[];
  values?: Record<string, React.ReactNode>;
  rows?: GridRow[];
  rowGroupLabel?: string;
};

export type ScoringGridProps = {
  title?: string;
  sections: GridSection[];
  className?: string;
};

export const ScoringGrid: React.FC<ScoringGridProps> = ({ title, sections, className }) => {
  return (
    <div className={className ?? ''}>
      {title ? <div className="mb-2 text-sm font-semibold text-gray-700">{title}</div> : null}
      <div className="flex flex-col gap-6">
        {sections.map((section) => {
          const { id, columns } = section;
          const isMultiRow = Array.isArray(section.rows) && section.rows.length > 0;
          const hasValue = (v: unknown) => v !== undefined && v !== null && `${v}`.length > 0;
          const filteredColumns = columns.filter((col) => {
            if (isMultiRow) {
              return section.rows!.some((row) => hasValue(row.values[col.key]));
            }
            const singleValues = section.values ?? {};
            return hasValue(singleValues[col.key]);
          });

          if (isMultiRow) {
            const { rows, rowGroupLabel } = section;
            return (
              <div key={id} className="rounded-lg border border-gray-200">
                {/* header row */}
                <div className="grid grid-cols-[140px_1fr] md:grid-cols-[160px_1fr]">
                  <div className="flex items-center justify-start px-3 text-xs font-medium text-gray-700">
                    {rowGroupLabel ?? ''}
                  </div>
                  <div
                    className="grid gap-px bg-[#F6F6FD] p-2 rounded-t-lg"
                    style={{
                      gridTemplateColumns: `repeat(${Math.max(filteredColumns.length, 1)}, minmax(0,1fr))`,
                    }}
                  >
                    {filteredColumns.map((col) => (
                      <div
                        key={col.key}
                        className="rounded-md bg-white px-3 py-3 text-xs font-medium text-gray-700 text-center"
                      >
                        {col.label}
                      </div>
                    ))}
                  </div>
                </div>

                {/* multiple value rows */}
                {rows!.map((row, idx) => (
                  <div
                    key={`${id}-row-${idx}`}
                    className={`grid grid-cols-[140px_1fr] md:grid-cols-[160px_1fr] border-t border-gray-200`}
                  >
                    <div className="flex items-center justify-start px-3 text-xs text-gray-600">
                      {row.label}
                    </div>
                    <div
                      className={`grid gap-px ${idx % 2 === 0 ? 'bg-[#F6F6FD]' : 'bg-white'} p-2 ${
                        idx === rows!.length - 1 ? 'rounded-b-lg' : ''
                      }`}
                      style={{
                        gridTemplateColumns: `repeat(${Math.max(filteredColumns.length, 1)}, minmax(0,1fr))`,
                      }}
                    >
                      {filteredColumns.map((col) => (
                        <div key={col.key} className="px-3 py-3 text-xs text-gray-700 text-center">
                          {row.values[col.key] ?? '—'}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          }

          // Single-row legacy mode
          const { values = {} } = section;
          return (
            <div key={id}>
              <div className="grid grid-cols-[140px_1fr] md:grid-cols-[160px_1fr]">
                <div className="h-full bg-transparent"></div>
                <div
                  className="grid gap-px bg-gray-100 p-2 rounded-t-lg"
                  style={{
                    gridTemplateColumns: `repeat(${Math.max(filteredColumns.length, 1)}, minmax(0,1fr))`,
                  }}
                >
                  {filteredColumns.map((col) => (
                    <div
                      key={col.key}
                      className="rounded-md bg-gray-50 px-3 py-3 text-xs font-medium text-gray-700 text-center"
                    >
                      {col.label}
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-[140px_1fr] md:grid-cols-[160px_1fr] border-t border-gray-200">
                <div
                  className="grid gap-px bg-gray-100 p-2 rounded-b-lg"
                  style={{
                    gridTemplateColumns: `repeat(${Math.max(filteredColumns.length, 1)}, minmax(0,1fr))`,
                  }}
                >
                  {filteredColumns.map((col) => (
                    <div
                      key={col.key}
                      className="rounded-md bg-white px-3 py-3 text-xs text-gray-700 text-center"
                    >
                      {values[col.key] ?? '—'}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ScoringGrid;
